<template>
  <div :class="{ main: true, mainHighlight: highlight?.length > 0 }">
    <div ref="highlightRef" class="highlight-shadow-input" v-html="highlight" />
    <textarea
      ref="inputRef"
      v-model="content"
      :rows="rows" 
      class="highlight-input"
      :disabled="disabled"
      :placeholder="placeholder"
      @input="changeText"
      @blur="handleBlur"
      @focus="handleFocus"
  ></textarea>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import Mark from 'mark.js'

const props = defineProps({
  value: String,
  prefix: String,
  highlight: String | Array,
  highlightIndex: Number,
  editable: {
    type: Boolean,
    default: true
  },
  placeholder: String,
  disabled: Boolean,
  rows: {
    type: Number,
    default: 3
  }
})
const emit = defineEmits(['input', 'focus', 'blur'])

const highlightRef = ref()
const inputRef = ref()
const content = ref(props.value)
const highlight = ref(props.value)

const onHighlight = (highlightStr, index = 0) => {
  const mask = new Mark(highlightRef.value)
  mask.mark(highlightStr, { separateWordSearch: false, acrossElements:true, className: `markd${(index % 8) + 1}` })
}

const adjustTextareaHeight = () => {
  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.style.height = 'auto'
      inputRef.value.style.height = content.value ? inputRef.value.scrollHeight + 'px' : '0px'
    }
  })
}

watch(() => props.value, (newVal) => {
  content.value = newVal
  highlight.value = newVal
  adjustTextareaHeight()
}, { immediate: true })

watch(() => props.disabled, (newVal) => {
  adjustTextareaHeight()
})

watch(() => props.highlight, (newVal) => {
  if (newVal) {
    nextTick(() => {
      const mask = new Mark(highlightRef.value);
      mask.unmark();
      const fileHighlight = newVal.filter(item => item.text && item.fileName) || []
      const generate = newVal.filter(item => item.text && item.color) || []

      fileHighlight.forEach((item, index) => {
        onHighlight(item.text, props.highlightIndex ?? index)
      })
      let replaceStr = ''
      generate.forEach((item, index) => {
        if (index === 0) {
          replaceStr = item.text
        } else if (index > 0 && (generate[index - 1].color === item.color)) {
          replaceStr += item.text
        } else {
          highlight.value = highlight.value.replace(replaceStr, `<span style="background: ${item.color}">${replaceStr}</span>`)
          replaceStr = ''
        }
        if (index === generate.length - 1 && replaceStr) {
          highlight.value = highlight.value.replace(replaceStr, `<span style="background: ${item.color}">${replaceStr}</span>`)
        }
      })
    })
  }
}, { deep: true })

const handleFocus = () => {
  emit('focus', content.value)
}

const handleBlur = () => {
  emit('blur', null)
}

const changeText = (e) => {
  highlight.value = e.target.value
  adjustTextareaHeight()
  emit('input', content.value)  
}

</script>
<style lang="scss" scoped>
.main {
  width: 100%;
  padding: 0 25px;
  box-sizing: border-box;
}

.highlight-input,
.highlight-shadow-input {
  position: relative;
  width: 100%;
  padding: 0;
  line-height: 20px;
  resize: none;
  transition: all 0.3s ease;
  min-height: 60px;
  height: auto;
  overflow: hidden;
  color: #444;
  font-family: PingFang SC;
  font-size: 14px;
  word-break: break-all;
  border: none;
  background: transparent;
  &::placeholder {
    color: #999;
  }
  &:focus {
    outline: none;
    box-shadow: none;
  }
}

.highlight-shadow-input {
  position: absolute;
  top: 0;
  left: 25px;
  display: none;
  width: calc(100% - 50px);
  white-space: pre-wrap;
  overflow-y: auto;
}

.mainHighlight {
  position: relative;
  .highlight-shadow-input {
    display: block;
    // z-index: 999;
  }
  .highlight-input {
    -webkit-text-fill-color: transparent;
  }
}

:deep(.markd1) {
  background: rgba(255,0,0,0.15);
}
:deep(.markd2) {
  background: #FFE893;
}
:deep(.markd3) {
  background: #FBB4A5;
}
:deep(.markd4) {
  background: #FB9EC6;
}
:deep(.markd5) {
  background: #FFCF9D;
}
:deep(.markd6) {
  background: #DE8F5F;
}
:deep(.markd7) {
  background: #C4E1F6;
}
:deep(.markd8) {
  background: #FFF0DC;
}
</style>